/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, NgZone} from '@angular/core';
import template from './email-translation-edit-page.component.html';
import * as _ from 'lodash';
import {AppEventBusService} from '../../common/events/app-event-bus.service';

import {AdminResourceEditPageBase} from '../../common/controller/admin-resource-edit-page-base';
import {LOCALES} from '../../common/constants/locales';
import {TeamResource} from '../../../components/api/resources/resource/team.resource';
import {
  applyDefaultEmailTranslationContent,
  getEmailTranslationTeamQueryParams,
  getEmailTranslationTypes,
  indexDefaultEmailTranslations,
  initEmailTranslationModel,
  normalizeEmailTemplateSelection,
  prepareEmailTranslationForSubmit,
  updateEmailTranslationInfo
} from './email-translation-commons';
import {EmailTemplateResource} from '../../../components/api/resources/resource/email-template.resource';
import {EmailTranslationTestResource} from '../../../components/api/resources/resource/email-translation.resource';
import {DefaultEmailTranslationsResource} from '../../../components/api/resources/resource/default-email-translations';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../../components/session/local-user-permissions';
import {LocalUserProfile} from '../../../components/session/local-user-profile';
import {UserProfileSaverService} from '../../../components/session/user-profile-saver.service';

@Component({
  standalone: false,
  selector: 'adn-email-translation-edit-page',
  template
})
export class EmailTranslationEditPageComponent extends AdminResourceEditPageBase {
  isMarketplace = false;
  isAdmin = false;
  demo = '';
  demoSuccess = false;
  demoError = false;
  allTeams: any[] = [];
  teamsLoaded = false;
  userInterfaces: any[] = [];
  userInterfacesLoaded = false;
  availableTemplates: any[] = [];
  templatesLoaded = false;
  defaultTranslationsByType: Record<string, any> = {};
  emailTypes: string[] = [];
  applications: string[] = [];
  readonly locales = LOCALES;

  constructor(
    eventBus: AppEventBusService,
    @Inject(UserProfileSaverService) UserProfileSaver: any,
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    @Inject(LocalUserPermissions) private LocalUserPermissions: any,
    @Inject(LocalUserProfile) private localUserProfile: any,
    @Inject(TeamResource) private Team: any,
    @Inject(EmailTemplateResource) private EmailTemplate: any,
    @Inject(EmailTranslationTestResource) private EmailTranslationTest: any,
    @Inject(DefaultEmailTranslationsResource) private defaultEmailTranslationsResource: any,
    ngZone: NgZone
  ) {
    super(eventBus, UserProfileSaver, LocalNetworkProfile, ngZone);
    this.isMarketplace = LocalNetworkProfile.isPureMarketplacePlatform();
    this.isAdmin = !!localUserProfile.get().supportUserId;
    this.demo = localUserProfile.get('username');
    this.emailTypes = getEmailTranslationTypes(LocalNetworkProfile);
    this.applications = LocalUserPermissions.getApps();
    this.loadTeams();
    this.loadUserInterfaces();
    this.loadDefaultTranslations();
    this.loadEmailTemplates();
  }

  get showTeamField(): boolean {
    return this.isMarketplace || !!this.editingModel?.team?.id;
  }

  compareTeams(team1: any, team2: any): boolean {
    return team1 && team2 ? team1.id === team2.id : team1 === team2;
  }

  hasApplication(app: string): boolean {
    return _.includes(this.editingModel?.applications, app);
  }

  toggleApplication(app: string, event: Event): void {
    if (!_.isArray(this.editingModel.applications)) {
      this.editingModel.applications = [];
    }
    const checked = (event.target as HTMLInputElement).checked;
    if (checked) {
      if (!this.hasApplication(app)) {
        this.editingModel.applications.push(app);
      }
    } else {
      _.pull(this.editingModel.applications, app);
    }
    this.markExternalFormDirty();
  }

  onMessageDefinitionChange(): void {
    applyDefaultEmailTranslationContent(this.editingModel, this.defaultTranslationsByType);
    this.markExternalFormDirty();
  }

  onSpecifyFieldChange(): void {
    applyDefaultEmailTranslationContent(this.editingModel, this.defaultTranslationsByType);
    this.markExternalFormDirty();
  }

  sendDemo(): void {
    this.demoSuccess = false;
    this.demoError = false;
    this.EmailTranslationTest.test(
      this.editingModel.id,
      this.localUserProfile.get().supportUserId ? this.demo : ''
    ).then(() => {
      this.demoSuccess = true;
    }).catch(() => {
      this.demoError = true;
    });
  }

  protected afterInitEditingModel(): void {
    initEmailTranslationModel(this.editingModel, this.defaultTranslationsByType);
    this.refreshAvailableTemplates();
  }

  protected afterInitAfterSave(saveObj?: any): void {
    updateEmailTranslationInfo(this.editingModel, this.defaultTranslationsByType);
    applyDefaultEmailTranslationContent(this.editingModel, this.defaultTranslationsByType);
    normalizeEmailTemplateSelection(this.editingModel, saveObj);
    this.refreshAvailableTemplates();
  }

  protected onBeforeSubmit(): void {
    prepareEmailTranslationForSubmit(this.editingModel, this.defaultTranslationsByType);
  }

  private loadTeams(): void {
    this.Team.query(getEmailTranslationTeamQueryParams(this.LocalUserPermissions)).$promise.then((page: any) => {
      this.allTeams = page.results || [];
      this.teamsLoaded = true;
    });
  }

  private loadUserInterfaces(): void {
    this.defaultEmailTranslationsResource.getUserInterfaces(this.LocalNetworkProfile.getNetworkId()).then((data: any) => {
      this.userInterfaces = data.results || [];
      this.userInterfacesLoaded = true;
    });
  }

  private loadDefaultTranslations(): void {
    this.defaultEmailTranslationsResource.get().then((defaults: any[]) => {
      this.defaultTranslationsByType = indexDefaultEmailTranslations(defaults);
      if (this.editingModel) {
        initEmailTranslationModel(this.editingModel, this.defaultTranslationsByType);
      }
    });
  }

  private loadEmailTemplates(): void {
    this.EmailTemplate.query().$promise.then((data: any) => {
      this.availableTemplates = this.filterAvailableTemplates(data.results || []);
      this.templatesLoaded = true;
    });
  }

  private refreshAvailableTemplates(): void {
    if (!this.templatesLoaded) {
      return;
    }
    this.EmailTemplate.query().$promise.then((data: any) => {
      this.availableTemplates = this.filterAvailableTemplates(data.results || []);
    });
  }

  private filterAvailableTemplates(templates: any[]): any[] {
    const selectedTemplateId = _.get(this.editingModel, ['emailTemplate', 'id']) || this.editingModel?.emailTemplate;
    return _.filter(templates, (template: any) => {
      return !template.validationWarnings.length || selectedTemplateId === template.id;
    });
  }
}
